/**
 * @fileoverview MockApp component that provides a mock environment for testing the Question component.
 * It wraps the Question component with necessary context providers and handles state management.
 *
 * @example
 * // Usage in a test file:
 * import { MockApp } from './MockApp'
 *
 * function TestQuestion() {
 *   return <MockApp assignmentId="123" question={...} isLegacyBattery={false} />
 * }
 */

import { useState } from 'react'

import { RTCContext } from '../RTCContext'
import { Question } from './Question'

import { DataContext } from '../DataContext'
import { MiddlewareContext } from '../MiddlewareContext'
import { useAssets } from '../hooks/useAssets'
import { useICEServers } from '../hooks/useICEServers'
import './App.module.css'
import { GlobalModalContext } from './GlobalModalContext'


/**
 * @interface AppProps
 * @description Props for the MockApp component.
 * @property {string} assignmentId - The ID of the assignment.
 * @property {Novoic.AnyQuestion} question - The question to be displayed.
 * @property {boolean} isLegacyBattery - Whether the battery is legacy.
 */
export interface AppProps {
    assignmentId: string
    question: Novoic.AnyQuestion
    isLegacyBattery: boolean
}

/**
 * @function MockApp
 * @description Provides a mock environment for testing the Question component.
 * Wraps the Question component with necessary context providers and handles state management.
 * @param {AppProps} props - The props for the MockApp component.
 * @returns {JSX.Element | null} The rendered MockApp component or null if assets are not loaded.
 */
export function MockApp({ assignmentId, isLegacyBattery = false, question }: AppProps) {
    const [isExpired, setIsExpired] = useState<boolean>(false)
    const iceServers = useICEServers()
    const areAssetsLoaded = useAssets()

    if (!areAssetsLoaded) {
        return null
    }

    return (
        <RTCContext.Provider value={{ iceServers }}>
            <DataContext.Provider value={{ assignmentId, standalone: true, setIsExpired }}>
                <MiddlewareContext.Provider
                    value={{
                        assignmentId,
                        isLegacyBattery,
                        question,
                        standalone: true,
                        isExpired,
                        errors: {},
                        submit: () => Promise.resolve(),
                        exit: () => Promise.resolve(),
                    }}
                >
                    <GlobalModalContext>
                        <Question />
                    </GlobalModalContext>
                </MiddlewareContext.Provider>
            </DataContext.Provider>
        </RTCContext.Provider>
    )
}
